import { useState, useRef, useEffect } from 'react';
import { useSitesStore } from '../stores/sitesStore';
import { useViewerStore } from '../stores/viewerStore';
import { useRackEditorStore } from '../stores/rackEditorStore';
import { endpoints, SMPLRSPACE_TOKEN } from '../config/api';
import CADPreview from './CADPreview';
import SmplrFloorPlan from './SmplrFloorPlan';
import LocationMapper from './LocationMapper';
import SVGFloorPlan from './SVGFloorPlan';

interface ZoneUtilization {
  zone: string;
  total_locations: number;
  occupied_locations: number;
  utilization_pct: number;
}

interface UtilizationData {
  site_code: string;
  zones: ZoneUtilization[];
  total_locations: number;
  occupied_locations: number;
  utilization_pct: number;
  last_updated: string;
}

type UploadStatus = 'idle' | 'uploading' | 'previewing' | 'processing' | 'complete' | 'error';

function Sidebar() {
  const [expanded, setExpanded] = useState(true);
  const [uploadStatus, setUploadStatus] = useState<UploadStatus>('idle');
  const [uploadProgress, setUploadProgress] = useState(0);
  const [uploadError, setUploadError] = useState<string | null>(null);
  const [currentFileId, setCurrentFileId] = useState<string | null>(null);
  const [currentPreviewId, setCurrentPreviewId] = useState<string | null>(null);
  const [utilization, setUtilization] = useState<UtilizationData | null>(null);
  const [usePreview, setUsePreview] = useState(true); // Toggle for preview mode
  const [showFloorPlan, setShowFloorPlan] = useState(false);
  const [floorPlanMode, setFloorPlanMode] = useState<'viewer' | 'editor'>('viewer');
  const [showLocationMapper, setShowLocationMapper] = useState(false);
  const [showSVGFloorPlan, setShowSVGFloorPlan] = useState(false);
  const fileInputRef = useRef<HTMLInputElement>(null);

  const {
    sites,
    selectedSite,
    buildings,
    selectedBuilding,
    loading,
    selectSite,
    selectBuilding,
  } = useSitesStore();

  const { layerVisibility, toggleLayer } = useViewerStore();
  const { openEditor: openRackEditor, isOpen: isRackEditorOpen } = useRackEditorStore();

  // Fetch utilization data when site changes
  useEffect(() => {
    if (!selectedSite) {
      setUtilization(null);
      return;
    }

    const fetchUtilization = async () => {
      try {
        const resp = await fetch(
          `${endpoints.sites}/${selectedSite.code}/utilization`,
          { credentials: 'include' }
        );
        if (resp.ok) {
          const data = await resp.json();
          setUtilization(data);
        }
      } catch (err) {
        console.log('Utilization data not available');
      }
    };

    fetchUtilization();
  }, [selectedSite]);

  const handleFileSelect = async (event: React.ChangeEvent<HTMLInputElement>) => {
    const file = event.target.files?.[0];
    if (!file || !selectedBuilding || !selectedSite) return;

    setUploadStatus('uploading');
    setUploadProgress(0);
    setUploadError(null);

    try {
      // Create FormData
      const formData = new FormData();
      formData.append('file', file);

      // Upload file - API expects site_id and building_id as query params
      const uploadUrl = new URL(endpoints.upload);
      uploadUrl.searchParams.set('site_id', selectedSite.id);
      uploadUrl.searchParams.set('building_id', selectedBuilding.id);

      const uploadResponse = await fetch(uploadUrl.toString(), {
        method: 'POST',
        credentials: 'include',
        body: formData,
      });

      if (!uploadResponse.ok) {
        const errorData = await uploadResponse.json();
        throw new Error(errorData.detail || 'Upload failed');
      }

      const uploadData = await uploadResponse.json();
      const fileId = uploadData.file_id;
      setCurrentFileId(fileId);
      setUploadProgress(50);

      // If preview mode is enabled, trigger preview extraction
      if (usePreview) {
        setUploadStatus('previewing');

        const previewResponse = await fetch(endpoints.preview, {
          method: 'POST',
          credentials: 'include',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify({
            source_file_id: fileId,
            building_id: selectedBuilding.id,
          }),
        });

        if (!previewResponse.ok) {
          const errorData = await previewResponse.json();
          throw new Error(errorData.detail || 'Preview extraction failed');
        }

        const previewData = await previewResponse.json();
        setCurrentPreviewId(previewData.preview_id);
        // Preview modal will be shown
        return;
      }

      // Direct processing (no preview)
      setUploadStatus('processing');
      await triggerDirectProcessing(fileId);

    } catch (error) {
      setUploadStatus('error');
      setUploadError(error instanceof Error ? error.message : 'Unknown error');
      setCurrentFileId(null);
      setCurrentPreviewId(null);
    }

    // Reset file input
    if (fileInputRef.current) {
      fileInputRef.current.value = '';
    }
  };

  const triggerDirectProcessing = async (fileId: string) => {
    if (!selectedBuilding) return;

    // Trigger processing
    const processResponse = await fetch(endpoints.process, {
      method: 'POST',
      credentials: 'include',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({
        source_file_id: fileId,
        building_id: selectedBuilding.id,
      }),
    });

    if (!processResponse.ok) {
      const errorData = await processResponse.json();
      throw new Error(errorData.detail || 'Processing failed to start');
    }

    // Poll for status
    pollProcessingStatus(fileId);
  };

  const pollProcessingStatus = (fileId: string) => {
    const poll = async () => {
      try {
        const statusResponse = await fetch(endpoints.fileStatus(fileId), {
          credentials: 'include',
        });

        if (!statusResponse.ok) {
          throw new Error('Failed to check status');
        }

        const statusData = await statusResponse.json();

        if (statusData.status === 'completed' || statusData.status === 'processed') {
          setUploadProgress(100);
          setUploadStatus('complete');
          setCurrentFileId(null);
        } else if (statusData.status === 'failed') {
          throw new Error(statusData.error_message || 'Processing failed');
        } else {
          // Still processing, poll again
          setUploadProgress(prev => Math.min(prev + 5, 95));
          setTimeout(poll, 3000);
        }
      } catch (error) {
        setUploadStatus('error');
        setUploadError(error instanceof Error ? error.message : 'Unknown error');
        setCurrentFileId(null);
      }
    };

    setTimeout(poll, 2000);
  };

  const handlePreviewConfirm = (_previewId: string) => {
    // Preview confirmed, processing has started
    setCurrentPreviewId(null);
    setUploadStatus('processing');
    if (currentFileId) {
      pollProcessingStatus(currentFileId);
    }
  };

  const handlePreviewCancel = () => {
    setCurrentPreviewId(null);
    setUploadStatus('idle');
    setCurrentFileId(null);
    setUploadProgress(0);
  };

  const handleOpenRackEditor = () => {
    if (selectedBuilding) {
      openRackEditor(selectedBuilding.id);
    }
  };

  const resetUpload = () => {
    setUploadStatus('idle');
    setUploadProgress(0);
    setUploadError(null);
    setCurrentFileId(null);
    setCurrentPreviewId(null);
  };

  return (
    <aside className={`sidebar ${expanded ? 'expanded' : 'collapsed'}`}>
      <div className="sidebar-header">
        <h1 className="sidebar-title">
          {expanded && 'Site Plans'}
        </h1>
        <button
          className="sidebar-toggle"
          onClick={() => setExpanded(!expanded)}
          title={expanded ? 'Collapse' : 'Expand'}
        >
          <svg
            width="20"
            height="20"
            viewBox="0 0 20 20"
            fill="currentColor"
            style={{ transform: expanded ? 'rotate(180deg)' : 'none' }}
          >
            <path
              fillRule="evenodd"
              d="M12.707 5.293a1 1 0 010 1.414L9.414 10l3.293 3.293a1 1 0 01-1.414 1.414l-4-4a1 1 0 010-1.414l4-4a1 1 0 011.414 0z"
              clipRule="evenodd"
            />
          </svg>
        </button>
      </div>

      {expanded && (
        <div className="sidebar-content">
          {/* Sites Section */}
          <section className="sidebar-section">
            <h2 className="section-title">Sites</h2>
            {loading ? (
              <div className="loading-indicator">Loading...</div>
            ) : (
              <ul className="site-list">
                {sites.map((site) => (
                  <li
                    key={site.id}
                    className={`site-item ${selectedSite?.id === site.id ? 'selected' : ''}`}
                    onClick={() => selectSite(site)}
                  >
                    <span className="site-code">{site.code}</span>
                    <span className="site-name">{site.name}</span>
                  </li>
                ))}
              </ul>
            )}
          </section>

          {/* Buildings Section */}
          {selectedSite && (
            <section className="sidebar-section">
              <h2 className="section-title">Buildings</h2>
              {buildings.length === 0 ? (
                <div className="empty-state">No buildings found</div>
              ) : (
                <ul className="building-list">
                  {buildings.map((building) => (
                    <li
                      key={building.id}
                      className={`building-item ${selectedBuilding?.id === building.id ? 'selected' : ''}`}
                      onClick={() => selectBuilding(building)}
                    >
                      <div className="building-info">
                        <span className="building-code">{building.code}</span>
                        <span className="building-name">{building.name}</span>
                      </div>
                      {building.building_type && (
                        <span className="building-type">{building.building_type}</span>
                      )}
                    </li>
                  ))}
                </ul>
              )}
            </section>
          )}

          {/* Utilization Section */}
          {selectedSite && utilization && (
            <section className="sidebar-section">
              <h2 className="section-title">Utilization</h2>
              <div className="utilization-summary">
                <div className="utilization-overall">
                  <div className="utilization-bar">
                    <div
                      className="utilization-fill"
                      style={{
                        width: `${utilization.utilization_pct}%`,
                        backgroundColor: utilization.utilization_pct > 80 ? '#e74c3c' :
                          utilization.utilization_pct > 60 ? '#f39c12' : '#27ae60'
                      }}
                    />
                  </div>
                  <div className="utilization-stats">
                    <span className="utilization-pct">{utilization.utilization_pct}%</span>
                    <span className="utilization-detail">
                      {utilization.occupied_locations.toLocaleString()} / {utilization.total_locations.toLocaleString()} locations
                    </span>
                  </div>
                </div>
                <div className="utilization-zones">
                  {utilization.zones.map((zone) => (
                    <div key={zone.zone} className="zone-row">
                      <span className="zone-name">{zone.zone}</span>
                      <div className="zone-bar-container">
                        <div className="zone-bar">
                          <div
                            className="zone-fill"
                            style={{
                              width: `${zone.utilization_pct}%`,
                              backgroundColor: zone.utilization_pct > 80 ? '#e74c3c' :
                                zone.utilization_pct > 60 ? '#f39c12' : '#27ae60'
                            }}
                          />
                        </div>
                        <span className="zone-pct">{zone.utilization_pct}%</span>
                      </div>
                    </div>
                  ))}
                </div>
              </div>
            </section>
          )}

          {/* Building Details */}
          {selectedBuilding && (
            <section className="sidebar-section">
              <h2 className="section-title">Details</h2>
              <div className="building-details">
                <div className="detail-row">
                  <span className="detail-label">Area</span>
                  <span className="detail-value">
                    {selectedBuilding.floor_area_sqft?.toLocaleString() || '-'} sq ft
                  </span>
                </div>
                <div className="detail-row">
                  <span className="detail-label">Floors</span>
                  <span className="detail-value">{selectedBuilding.num_floors}</span>
                </div>
                <div className="detail-row">
                  <span className="detail-label">Height</span>
                  <span className="detail-value">
                    {selectedBuilding.height_ft || '-'} ft
                  </span>
                </div>
              </div>
            </section>
          )}

          {/* CAD Upload Section */}
          {selectedBuilding && (
            <section className="sidebar-section">
              <h2 className="section-title">CAD Upload</h2>
              <div className="upload-section">
                {uploadStatus === 'idle' && (
                  <>
                    <p className="upload-hint">
                      Upload DXF or DWG files to generate 3D models
                    </p>
                    <label className="preview-toggle">
                      <input
                        type="checkbox"
                        checked={usePreview}
                        onChange={(e) => setUsePreview(e.target.checked)}
                      />
                      <span>Preview before processing</span>
                    </label>
                    <input
                      ref={fileInputRef}
                      type="file"
                      accept=".dxf,.dwg"
                      onChange={handleFileSelect}
                      className="file-input"
                      id="cad-file-input"
                    />
                    <label htmlFor="cad-file-input" className="upload-button">
                      <svg width="16" height="16" viewBox="0 0 16 16" fill="currentColor">
                        <path d="M7.5 1.5v10a.5.5 0 001 0v-10l2.5 2.5a.5.5 0 00.707-.707l-3.5-3.5a.5.5 0 00-.707 0l-3.5 3.5a.5.5 0 00.707.707L7.5 1.5z"/>
                        <path d="M2 14v-3.5a.5.5 0 011 0V14h10v-3.5a.5.5 0 011 0V14a1 1 0 01-1 1H3a1 1 0 01-1-1z"/>
                      </svg>
                      Select File
                    </label>
                  </>
                )}

                {(uploadStatus === 'uploading' || uploadStatus === 'processing' || uploadStatus === 'previewing') && (
                  <div className="upload-progress">
                    <div className="progress-bar">
                      <div
                        className="progress-fill"
                        style={{ width: `${uploadProgress}%` }}
                      />
                    </div>
                    <span className="progress-label">
                      {uploadStatus === 'uploading' ? 'Uploading...' :
                       uploadStatus === 'previewing' ? 'Extracting preview...' :
                       'Processing...'}
                    </span>
                  </div>
                )}

                {uploadStatus === 'complete' && (
                  <div className="upload-complete">
                    <svg width="20" height="20" viewBox="0 0 20 20" fill="currentColor" className="success-icon">
                      <path fillRule="evenodd" d="M10 18a8 8 0 100-16 8 8 0 000 16zm3.707-9.293a1 1 0 00-1.414-1.414L9 10.586 7.707 9.293a1 1 0 00-1.414 1.414l2 2a1 1 0 001.414 0l4-4z" clipRule="evenodd"/>
                    </svg>
                    <span>Processing complete!</span>
                    <button className="reset-button" onClick={resetUpload}>
                      Upload Another
                    </button>
                  </div>
                )}

                {uploadStatus === 'error' && (
                  <div className="upload-error">
                    <svg width="20" height="20" viewBox="0 0 20 20" fill="currentColor" className="error-icon">
                      <path fillRule="evenodd" d="M18 10a8 8 0 11-16 0 8 8 0 0116 0zm-7 4a1 1 0 11-2 0 1 1 0 012 0zm-1-9a1 1 0 00-1 1v4a1 1 0 102 0V6a1 1 0 00-1-1z" clipRule="evenodd"/>
                    </svg>
                    <span className="error-message">{uploadError}</span>
                    <button className="reset-button" onClick={resetUpload}>
                      Try Again
                    </button>
                  </div>
                )}
              </div>
            </section>
          )}

          {/* Rack Placement Tool */}
          {selectedBuilding && (
            <section className="sidebar-section">
              <h2 className="section-title">Rack Placement</h2>
              <button
                className="rack-editor-button"
                onClick={handleOpenRackEditor}
                disabled={isRackEditorOpen}
              >
                <svg width="16" height="16" viewBox="0 0 16 16" fill="currentColor">
                  <path d="M2 2h4v4H2V2zm0 6h4v6H2V8zm6-6h6v6H8V2zm0 8h6v4H8v-4z"/>
                </svg>
                {isRackEditorOpen ? 'Editor Open' : 'Edit Racks'}
              </button>
              <p className="upload-hint">
                Design and configure rack rows with location codes
              </p>
            </section>
          )}

          {/* Location Mapper Tool */}
          {selectedSite && (
            <section className="sidebar-section">
              <h2 className="section-title">Location Mapper</h2>
              <button
                className="floor-plan-button"
                onClick={() => setShowLocationMapper(true)}
              >
                <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                  <path d="M21 10c0 7-9 13-9 13s-9-6-9-13a9 9 0 0 1 18 0z" />
                  <circle cx="12" cy="10" r="3" />
                </svg>
                Map Locations
              </button>
              <p className="upload-hint">
                Interactively map Odoo locations to floor plan coordinates
              </p>
            </section>
          )}

          {/* Floor Plan - SVG viewer (always available when site selected) */}
          {selectedSite && (
            <section className="sidebar-section">
              <h2 className="section-title">Floor Plan</h2>
              <div className="floor-plan-buttons">
                <button
                  className="floor-plan-button"
                  onClick={() => setShowSVGFloorPlan(true)}
                >
                  <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                    <rect x="3" y="3" width="18" height="18" rx="2" />
                    <path d="M3 9h18M9 21V9" />
                  </svg>
                  View Floor Plan
                </button>
                {selectedBuilding?.smplr_space_id && SMPLRSPACE_TOKEN && (
                  <>
                    <button
                      className="floor-plan-button"
                      onClick={() => {
                        setFloorPlanMode('viewer');
                        setShowFloorPlan(true);
                      }}
                    >
                      <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                        <path d="M21 16V8a2 2 0 0 0-1-1.73l-7-4a2 2 0 0 0-2 0l-7 4A2 2 0 0 0 3 8v8a2 2 0 0 0 1 1.73l7 4a2 2 0 0 0 2 0l7-4A2 2 0 0 0 21 16z" />
                      </svg>
                      3D View
                    </button>
                    <button
                      className="floor-plan-button floor-plan-edit"
                      onClick={() => {
                        setFloorPlanMode('editor');
                        setShowFloorPlan(true);
                      }}
                    >
                      <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                        <path d="M11 4H4a2 2 0 0 0-2 2v14a2 2 0 0 0 2 2h14a2 2 0 0 0 2-2v-7" />
                        <path d="M18.5 2.5a2.121 2.121 0 0 1 3 3L12 15l-4 1 1-4 9.5-9.5z" />
                      </svg>
                      Edit
                    </button>
                  </>
                )}
              </div>
              <p className="upload-hint">
                2D warehouse layout with inventory overlay
              </p>
            </section>
          )}

          {/* CAD Preview Modal */}
          {currentPreviewId && selectedBuilding && (
            <CADPreview
              previewId={currentPreviewId}
              buildingId={selectedBuilding.id}
              onConfirm={handlePreviewConfirm}
              onCancel={handlePreviewCancel}
            />
          )}

          {/* Smplrspace Floor Plan Modal */}
          {showFloorPlan && selectedBuilding?.smplr_space_id && (
            <SmplrFloorPlan
              spaceId={selectedBuilding.smplr_space_id}
              mode={floorPlanMode}
              onClose={() => setShowFloorPlan(false)}
              onDataChange={(data) => {
                console.log('Floor plan data updated:', data);
                // TODO: Sync data to backend
              }}
            />
          )}

          {/* Location Mapper Modal */}
          {showLocationMapper && selectedSite && (
            <div className="modal-overlay">
              <LocationMapper
                warehouseCode={selectedSite.code}
                onSave={(mappings) => {
                  console.log('Location mappings saved:', mappings.length);
                }}
                onClose={() => setShowLocationMapper(false)}
              />
            </div>
          )}

          {/* SVG Floor Plan Modal */}
          {showSVGFloorPlan && selectedSite && (
            <div className="modal-overlay">
              <SVGFloorPlan
                warehouseCode={selectedSite.code}
                onClose={() => setShowSVGFloorPlan(false)}
              />
            </div>
          )}

          {/* Layer Controls */}
          <section className="sidebar-section">
            <h2 className="section-title">Layers</h2>
            <div className="layer-controls">
              <label className="layer-toggle">
                <input
                  type="checkbox"
                  checked={layerVisibility.model}
                  onChange={() => toggleLayer('model')}
                />
                <span>3D Buildings</span>
              </label>
              <label className="layer-toggle">
                <input
                  type="checkbox"
                  checked={layerVisibility.satellite}
                  onChange={() => toggleLayer('satellite')}
                />
                <span>Satellite Imagery</span>
              </label>
              <label className="layer-toggle">
                <input
                  type="checkbox"
                  checked={layerVisibility.zones}
                  onChange={() => toggleLayer('zones')}
                />
                <span>Zone Boundaries</span>
              </label>
              <label className="layer-toggle">
                <input
                  type="checkbox"
                  checked={layerVisibility.navigation}
                  onChange={() => toggleLayer('navigation')}
                />
                <span>Navigation Paths</span>
              </label>
            </div>
          </section>
        </div>
      )}
    </aside>
  );
}

export default Sidebar;
